import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Save } from 'lucide-react';
import { toast } from 'react-hot-toast';
import DataTable from '../../components/common/DataTable';
import useAuthStore from '../../store/authStore';

const tabs = [
  { id: 'ACCOUNT', label: 'Müşteri Evrakları' },
  { id: 'OPERATION', label: 'Operasyon Evrakları' },
  { id: 'COMPANY', label: 'Şirket Evrakları' }
];

const emptyForm = {
  name: '',
  isMandatory: false,
  hasExpiryDate: false,
  uploadLimitMb: 10,
  acl: { viewRoles: [], uploadRoles: [], deleteRoles: [] },
  isActive: true
};

const toggleRole = (list, roleId) => (
  list.includes(roleId) ? list.filter((id) => id !== roleId) : [...list, roleId]
);

const normalizeRoleName = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/ı/g, 'i')
  .replace(/[^a-z0-9]/g, '');

const normalizeAcl = (acl = {}) => ({
  viewRoles: (acl.viewRoles || []).map((role) => role._id || role),
  uploadRoles: (acl.uploadRoles || []).map((role) => role._id || role),
  deleteRoles: (acl.deleteRoles || []).map((role) => role._id || role)
});

const DocumentSettings = () => {
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const [activeCategory, setActiveCategory] = useState('ACCOUNT');
  const [documentTypes, setDocumentTypes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const canWriteDocumentDefinitions = hasPermission('documents', 'write') || hasPermission('document_definition', 'write');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [typeRes, roleRes] = await Promise.all([
        axios.get('/api/documents/types', { params: { category: activeCategory } }),
        axios.get('/api/roles')
      ]);
      setDocumentTypes(typeRes.data.data || []);
      setRoles((roleRes.data.data || roleRes.data || []).filter((role) => normalizeRoleName(role?.name) !== 'superadmin'));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Evrak tanımları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadData();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [loadData]);

  const setAclRole = (aclKey, roleId) => {
    setForm((current) => ({
      ...current,
      acl: {
        ...current.acl,
        [aclKey]: toggleRole(current.acl[aclKey] || [], roleId)
      }
    }));
  };

  const startEdit = useCallback((row) => {
    if (!canWriteDocumentDefinitions) return;

    setEditingId(row._id);
    setForm({
      name: row.name,
      isMandatory: Boolean(row.isMandatory),
      hasExpiryDate: Boolean(row.hasExpiryDate),
      uploadLimitMb: row.uploadLimitMb || 10,
      acl: normalizeAcl(row.acl),
      isActive: row.isActive !== false
    });
  }, [canWriteDocumentDefinitions]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canWriteDocumentDefinitions) {
      toast.error('Evrak tanımı kaydetme yetkiniz yok.');
      return;
    }

    const payload = { ...form, category: activeCategory };

    try {
      if (editingId) {
        await axios.put(`/api/documents/types/${editingId}`, payload);
      } else {
        await axios.post('/api/documents/types', payload);
      }
      toast.success('Evrak tipi kaydedildi.');
      resetForm();
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Evrak tipi kaydedilemedi.');
    }
  };

  const columns = useMemo(() => [
    { key: 'name', header: 'Evrak Tipi', cellClassName: 'text-sm font-black text-slate-800' },
    {
      key: 'flags',
      header: 'Özellikler',
      render: (row) => (
        <div className="flex flex-wrap gap-2">
          {row.isMandatory && <span className="rounded-lg bg-rose-50 px-2 py-1 text-[10px] font-black text-rose-700">Zorunlu</span>}
          {row.hasExpiryDate && <span className="rounded-lg bg-amber-50 px-2 py-1 text-[10px] font-black text-amber-700">Süreli</span>}
          <span className="rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-black text-slate-600">{row.uploadLimitMb} MB</span>
        </div>
      )
    },
    canWriteDocumentDefinitions && {
      key: 'actions',
      header: 'Aksiyon',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      render: (row) => (
        <button type="button" onClick={() => startEdit(row)} className="rounded-xl bg-slate-900 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-white">
          Düzenle
        </button>
      )
    }
  ].filter(Boolean), [canWriteDocumentDefinitions, startEdit]);

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-black text-slate-900">Evrak Tanımları</h1>
        <p className="text-sm font-medium text-slate-500">Cari, proje ve şirket evrak tipleri ve ACL matrisi</p>
      </div>

      <div className="flex gap-2 rounded-2xl bg-slate-100 p-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => {
              setActiveCategory(tab.id);
              resetForm();
            }}
            className={`rounded-xl px-4 py-2 text-xs font-black uppercase tracking-widest ${activeCategory === tab.id ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className={`grid gap-6 ${canWriteDocumentDefinitions ? 'xl:grid-cols-[1fr_420px]' : ''}`}>
        <div className="overflow-hidden rounded-[2rem] border border-slate-100 bg-white shadow-sm">
          <DataTable columns={columns} data={documentTypes} loading={loading} emptyText="Evrak tipi bulunamadı" />
        </div>

        {canWriteDocumentDefinitions && (
          <form onSubmit={handleSubmit} className="space-y-4 rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-black text-slate-800">{editingId ? 'Evrak Tipini Düzenle' : 'Yeni Evrak Tipi'}</h2>
            <input
              value={form.name}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              placeholder="Evrak tipi adı"
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none"
            />
            <div className="grid grid-cols-3 gap-2">
              <label className="flex items-center gap-2 rounded-2xl bg-slate-50 p-3 text-xs font-bold text-slate-600">
                <input type="checkbox" checked={form.isMandatory} onChange={(event) => setForm((current) => ({ ...current, isMandatory: event.target.checked }))} />
                Zorunlu
              </label>
              <label className="flex items-center gap-2 rounded-2xl bg-slate-50 p-3 text-xs font-bold text-slate-600">
                <input type="checkbox" checked={form.hasExpiryDate} onChange={(event) => setForm((current) => ({ ...current, hasExpiryDate: event.target.checked }))} />
                Süreli
              </label>
              <input
                type="number"
                min="1"
                value={form.uploadLimitMb}
                onChange={(event) => setForm((current) => ({ ...current, uploadLimitMb: event.target.value }))}
                className="rounded-2xl border border-slate-200 bg-slate-50 px-3 text-xs font-black outline-none"
              />
            </div>

            <div className="overflow-hidden rounded-2xl border border-slate-100">
              <div className="grid grid-cols-4 bg-slate-50 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                <span>Rol</span><span>Görür</span><span>Ekler</span><span>Siler</span>
              </div>
              {roles.map((role) => (
                <div key={role._id} className="grid grid-cols-4 items-center border-t border-slate-50 px-3 py-2 text-xs font-bold text-slate-600">
                  <span>{role.name}</span>
                  {['viewRoles', 'uploadRoles', 'deleteRoles'].map((aclKey) => (
                    <input
                      key={aclKey}
                      type="checkbox"
                      checked={(form.acl[aclKey] || []).includes(role._id)}
                      onChange={() => setAclRole(aclKey, role._id)}
                    />
                  ))}
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <button type="submit" className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-slate-900 py-3 text-xs font-black uppercase tracking-widest text-white">
                <Save size={16} /> Kaydet
              </button>
              {editingId && (
                <button type="button" onClick={resetForm} className="rounded-2xl bg-slate-100 px-4 py-3 text-xs font-black text-slate-500">
                  Vazgeç
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default DocumentSettings;
